import { useContext, useMemo, useState } from 'react';
import { Col } from 'react-bootstrap';
import Dropzone from 'react-dropzone';
import { read, utils } from 'xlsx';
import { parseBlockData } from '../../global/BlockLayoutHelper';
import Constants from 'config/Constants';
import getTemplateParameters from '../../../components/SettingsSidebar/views/TemplateConstants';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { MAX_ROWS } from 'pages/documents_v2/views/edit_area/blocks/block_types/BlockLayoutBlock';
import { EditAreaContext } from 'pages/documents_v2/views/edit_area/context/EditAreaContext';
import { useGetDocument } from 'pages/documents_v2/hooks/useGetDocument';
import { useGetSections } from 'pages/documents_v2/hooks/useGetSections';

export function ImportCSVFileAsBlockLayoutContent({
    handleClose,
    setSubmitting,
    values,
    baseVariantId,
    forceReadOnly = false,
}) {
    const { t } = useTranslation('documents');
    const [file, setFile] = useState(null);
    const { insertBlocks, generateKey } = useContext(EditAreaContext);
    const document = useGetDocument(undefined);
    const sections = useGetSections(undefined, true);

    const documentVariants = useMemo(() => {
        if (values.variants.includes(baseVariantId)) {
            return values.variants;
        }

        return [...values.variants, baseVariantId];
    }, [values.variants, baseVariantId]);

    if (!document) {
        return null;
    }

    return (
        <div className="mt-3">
            <Dropzone maxFiles={1} accept={{ 'text/csv': ['.csv'] }} onDrop={handleDrop}>
                {({ getRootProps, getInputProps }) => (
                    <div {...getRootProps({ style })}>
                        <input {...getInputProps()} />
                        {file ? (
                            <Col className="outlined">
                                {t(
                                    'document.navbar.main.editor.left.subnav.addBlock.blockLayout.dropZone.selectedFile',
                                    {
                                        filename: file.name,
                                    },
                                )}
                            </Col>
                        ) : (
                            <div className="text-muted font-small-weighted pr-2">
                                {t(
                                    'document.navbar.main.editor.left.subnav.addBlock.blockLayout.dropZone.placeholderText',
                                    {
                                        max_rows: MAX_ROWS,
                                    },
                                )}
                            </div>
                        )}
                    </div>
                )}
            </Dropzone>
        </div>
    );

    function handleDrop(acceptedFiles) {
        if (acceptedFiles.length > 0) {
            const file = acceptedFiles[0];
            setFile(file);
            setSubmitting(true);

            const reader = new FileReader();

            reader.onload = async (e) => {
                const data = e.target.result;
                const workbook = read(new Uint8Array(data), { type: 'array', raw: true, FS: ';' });
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                const csvData = utils.sheet_to_json(worksheet, {
                    header: 1,
                    blankrows: false,
                    defval: '',
                    rawNumbers: true,
                });

                const rawContent = await file.text();

                insertBlocks(addBlocksFromCSV(csvData, rawContent));
                handleClose();
            };

            reader.readAsArrayBuffer(file);
        }
    }

    function addBlocksFromCSV(data = [], csvContent) {
        const newAreaBlocks = [];
        const extraKeys = [];

        const insertAsReadOnly = data.length > MAX_ROWS || forceReadOnly;
        const crossRefList = createCrossReferencesList(sections);

        let layoutBlockContent = {
            rows: data.length,
            columns: data.length > 0 ? data[0].length : 0,
            data: null,
            csvData: null,
            readOnly: insertAsReadOnly,
        };

        if (insertAsReadOnly) {
            layoutBlockContent.csvData = applyCrossReferences(csvContent, crossRefList, document.id);
        } else {
            let content = [];

            data.forEach((row) => {
                let contentRow = [];

                row.forEach((cell) => {
                    if (cell === '') {
                        contentRow.push([]);
                    } else {
                        const randomKey = generateKey(extraKeys);
                        extraKeys.push(randomKey);

                        const newBlock = {
                            id: null,
                            key: randomKey,
                            documentVariants,
                            latestContent: `${applyCrossReferences(cell, crossRefList, document.id)}`,
                            properties: {
                                textLayout: 'default',
                                templateParameters: justTheParameters(Constants.blockTypes.text),
                            },
                            type: Constants.blockTypes.text,
                        };
                        newAreaBlocks.push(newBlock);
                        contentRow.push([{ id: newBlock.key, new: true }]);
                    }
                });

                content.push(contentRow);
            });

            layoutBlockContent.data = content;
        }

        const newLayoutBlockKey = generateKey(extraKeys);
        extraKeys.push(newLayoutBlockKey);

        const newLayoutBlock = {
            id: null,
            key: newLayoutBlockKey,
            documentVariants,
            latestContent: parseBlockData(layoutBlockContent),
            properties: {
                type: 'default',
                showTableHeader: true,
                highlightFirstColumn: true,
                templateParameters: justTheParameters(Constants.blockTypes.blockLayout),
            },
            type: Constants.blockTypes.blockLayout,
        };

        newAreaBlocks.push(newLayoutBlock);

        return newAreaBlocks;
    }
}

function justTheParameters({ blockType }) {
    const templateParameters = getTemplateParameters(blockType, {});
    return Object.assign({}, ...Object.values(templateParameters));
}

function createCrossReferencesList(sections = []) {
    const crossReferencesList = [];

    sections.forEach((section) => {
        if (section.deletedAt === null) {
            crossReferencesList.push({
                title: section.title,
                id: section.id,
                type: 'section',
            });
        }
        section.areas.forEach((area) => {
            if (area.deletedAt === null) {
                crossReferencesList.push({
                    title: area.title,
                    id: area.id,
                    type: 'area',
                });
            }
        });
    });

    return crossReferencesList;
}

function applyCrossReferences(cell, crossReferencesList, documentId) {
    if (crossReferencesList.length === 0) {
        return cell;
    }

    cell = _.toString(cell);

    cell = cell.replace(new RegExp(/\{(.*?)}/gm), (match, group1) => {
        const ref = crossReferencesList.find((item) => item.title === group1);

        if (ref) {
            return `[Ref id=${ref.id} type=${ref.type} document=${documentId}]`;
        }

        return match;
    });

    return cell;
}

const style = {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    padding: '20px',
    borderWidth: 2,
    borderRadius: 2,
    borderColor: '#eeeeee',
    borderStyle: 'dashed',
    backgroundColor: '#fafafa',
    color: '#bdbdbd',
    outline: 'none',
    transition: 'border .24s ease-in-out',
};
